import type { TableOrder } from "@hobbyshopify/backend-client";
import type { Stripe } from "@stripe/stripe-js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatAmount } from "../core/primitives/money";
import { tableCharge } from "../core/primitives/vendor-show";
import { HoldCountdown } from "./HoldCountdown";
import { holdScroll } from "./scroll-lock";
import { PaymentError, TablePaymentForm } from "./TablePaymentForm";

interface TableCheckoutSheetProps {
  order: TableOrder;
  /** The show's title and dates; null once the show is no longer listed. */
  show: string | null;
  /** Stripe.js for the host's account; resolves null where payments cannot be taken. */
  stripe: Promise<Stripe | null>;
  clientSecret: string;
  error: string | null;
  /** A card is with Stripe, or the table is being given up: nothing closes the sheet or starts a card. */
  busy: boolean;
  cancelling: boolean;
  /** Closing gives the table up. */
  onCancel(): void;
  onStart(): boolean;
  onFailed(message: string): void;
  onConfirmed(): void;
}

/**
 * Paying for a held table over the page, as a Live break's spots are paid for in the storefront.
 * Drawn on the body, with the app behind it inert and still: what Stripe.js adds to the body, its
 * 3DS challenge among them, stays usable.
 */
export function TableCheckoutSheet(props: TableCheckoutSheetProps) {
  const { order, show, stripe, error, busy, cancelling, onCancel } = props;
  const panel = useRef<HTMLDivElement>(null);
  const [missing, setMissing] = useState(false);

  useLayoutEffect(() => {
    const app = document.getElementById("root");
    const madeInert = app !== null && !app.hasAttribute("inert");
    if (madeInert) app.setAttribute("inert", "");
    const release = holdScroll();
    panel.current?.focus();
    return () => {
      if (madeInert) app.removeAttribute("inert");
      release();
    };
  }, []);

  useEffect(() => {
    let live = true;
    setMissing(false);
    stripe.then(
      (loaded) => {
        if (live && !loaded) setMissing(true);
      },
      () => {
        if (live) setMissing(true);
      },
    );
    return () => {
      live = false;
    };
  }, [stripe]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const charge = tableCharge(order);
  const money = (cents: number) => formatAmount(cents, order.currency);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-6">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="table-checkout-title"
        tabIndex={-1}
        className="flex max-h-[92dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-card border border-line bg-app pb-[env(safe-area-inset-bottom)] outline-none sm:rounded-card sm:pb-0"
      >
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0 flex-1">
            <p id="table-checkout-title" className="text-[15px] font-semibold">
              Pay for your table
            </p>
            <HoldCountdown expiresAt={order.expiresAt} />
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="shrink-0 text-sm text-muted hover:text-white"
          >
            {cancelling ? "Cancelling…" : "Cancel"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {missing ? (
            <div className="space-y-3 p-6 text-center">
              <p className="text-sm text-danger">Payments cannot be taken here right now.</p>
              <p className="text-sm text-muted">
                Reload the page to try again. Unpaid, the table goes back on sale when the hold runs out.
              </p>
              {error && <PaymentError message={error} />}
            </div>
          ) : (
            <div className="space-y-4 p-4">
              <div className="rounded-card border border-line bg-surface p-4">
                <p className="text-sm font-semibold">Your table</p>
                <div className="mt-3 flex justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-muted">
                    Table {order.tableLabel} · {order.kindName}
                  </span>
                  <span className="shrink-0 tabular-nums">{money(charge.priceCents)}</span>
                </div>
                {charge.feesCents > 0 && (
                  <div className="mt-1 flex justify-between gap-3 text-sm text-muted">
                    <span>Fees</span>
                    <span className="shrink-0 tabular-nums">{money(charge.feesCents)}</span>
                  </div>
                )}
                <div className="mt-2 flex justify-between border-t border-line pt-2 text-sm font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums">{money(charge.totalCents)}</span>
                </div>
                <p className="mt-3 text-xs leading-snug text-muted">
                  {show && `${show}. `}One charge now. Once Stripe confirms it, the table is yours, and it becomes your
                  booth there for the show's days.
                </p>
              </div>

              <TablePaymentForm
                key={props.clientSecret}
                stripe={stripe}
                clientSecret={props.clientSecret}
                amount={money(charge.totalCents)}
                error={error}
                disabled={busy}
                onStart={props.onStart}
                onFailed={props.onFailed}
                onConfirmed={props.onConfirmed}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
